import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import PageHeader from "@/components/shared/PageHeader";
import QuickLoader from "@/components/shared/QuickLoader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Bug, Plus, X, CheckCircle2, AlertTriangle, MapPin,
  Calendar, Camera, ClipboardCheck, AlertCircle
} from "lucide-react";

const PEST_TYPES = [
  { value: "rodents", label: "Rosegadors" },
  { value: "cockroaches", label: "Paneroles" },
  { value: "flies", label: "Mosques" },
  { value: "ants", label: "Formigues" },
  { value: "birds", label: "Ocells" },
  { value: "stored_product", label: "Insectes de productes emmagatzemats" },
  { value: "other", label: "Altres" },
];
const SEVERITY_OPTS = [
  { value: "low", label: "Baixa", color: "bg-blue-100 text-blue-700" },
  { value: "medium", label: "Mitjana", color: "bg-amber-100 text-amber-700" },
  { value: "high", label: "Alta", color: "bg-orange-100 text-orange-700" },
  { value: "critical", label: "Crítica", color: "bg-red-100 text-red-700" },
];
const LOCATIONS = ["Cuina", "Magatzem", "Menjador", "Lavabos", "Terrassa", "Barra", "Entrada", "Exterior", "Oficina"];

export default function PestControl() {
  const { currentVenue } = useVenue();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ pest_type: "rodents", severity: "medium", location: "Cuina", description: "", corrective_action: "", photo_url: "" });

  useEffect(() => { if (currentVenue) load(); }, [currentVenue]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await hosys.entities.getAll("pest_incidents", currentVenue.id, "-date");
      setIncidents(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.description) return;
    await hosys.entities.create("pest_incidents", {
      ...form, venue_id: currentVenue.id,
      date: new Date().toISOString().slice(0, 10),
      status: "open",
      verified: false,
    });
    setShowForm(false);
    setForm({ pest_type: "rodents", severity: "medium", location: "Cuina", description: "", corrective_action: "", photo_url: "" });
    load();
  };

  const handleClose = async (inc) => {
    if (!inc.corrective_action) {
      console.warn("Cal afegir una acció correctiva abans de tancar la incidència.");
      return;
    }
    await hosys.entities.update("pest_incidents", inc.id || inc.pest_incident_id, {
      status: "closed", verified: true, verified_at: new Date().toISOString(),
    });
    load();
  };

  const handleVerify = async (inc) => {
    await hosys.entities.update("pest_incidents", inc.id || inc.pest_incident_id, {
      verified: true, verified_at: new Date().toISOString(),
    });
    load();
  };

  if (loading) return <QuickLoader />;

  const openCount = incidents.filter(i => i.status !== "closed" && i.status !== "resolved").length;
  const critCount = incidents.filter(i => (i.severity === "critical" || i.severity === "high") && i.status !== "closed").length;

  return (
    <div className="space-y-5" data-testid="pest-control-page">
      <PageHeader title="Control de Plagues" subtitle="Registre i seguiment d'incidències de plagues (DDD)" />

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="p-4 border-0 shadow-sm bg-gray-50 text-center">
          <p className="text-2xl font-bold text-gray-800">{incidents.length}</p>
          <p className="text-xs text-gray-500">Total registres</p>
        </Card>
        <Card className={`p-4 border-0 shadow-sm text-center ${openCount > 0 ? "bg-amber-50" : "bg-emerald-50"}`}>
          <p className={`text-2xl font-bold ${openCount > 0 ? "text-amber-700" : "text-emerald-700"}`}>{openCount}</p>
          <p className="text-xs text-gray-500">Obertes</p>
        </Card>
        <Card className={`p-4 border-0 shadow-sm text-center ${critCount > 0 ? "bg-red-50" : "bg-emerald-50"}`}>
          <p className={`text-2xl font-bold ${critCount > 0 ? "text-red-700" : "text-emerald-700"}`}>{critCount}</p>
          <p className="text-xs text-gray-500">Crítiques</p>
        </Card>
        <Card className="p-4 border-0 shadow-sm">
          <Button onClick={() => setShowForm(!showForm)} className="w-full bg-[#7B2D3F] hover:bg-[#7B2D3F]/90" data-testid="new-pest-btn">
            <Plus className="w-4 h-4 mr-1" /> Nova incidència
          </Button>
        </Card>
      </div>

      {/* Critical Alert */}
      {critCount > 0 && (
        <div className="rounded-2xl bg-red-50 border border-red-200 p-4 flex items-start gap-3" data-testid="pest-critical-alert">
          <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
          <div>
            <p className="text-sm font-bold text-red-800">{critCount} incidència(es) crítica(ques) oberta(es)</p>
            <p className="text-xs text-red-600">El compliment APPCC està bloquejat fins que es resolguin.</p>
          </div>
        </div>
      )}

      {/* New Incident Form */}
      {showForm && (
        <Card className="p-5 border-0 shadow-sm" data-testid="pest-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-gray-900">Nova Incidència de Plaga</h3>
            <button onClick={() => setShowForm(false)}><X className="w-4 h-4 text-gray-400" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Tipus de plaga</label>
              <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.pest_type} onChange={e => setForm({...form, pest_type: e.target.value})} data-testid="pest-type-select">
                {PEST_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Gravetat</label>
              <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.severity} onChange={e => setForm({...form, severity: e.target.value})} data-testid="pest-severity-select">
                {SEVERITY_OPTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Ubicació</label>
              <select className="w-full border rounded-lg px-3 py-2 text-sm" value={form.location} onChange={e => setForm({...form, location: e.target.value})} data-testid="pest-location-select">
                {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <Textarea placeholder="Descripció de la incidència..." className="mb-3" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="pest-description" />
          <Textarea placeholder="Acció correctiva aplicada..." className="mb-3" value={form.corrective_action} onChange={e => setForm({...form, corrective_action: e.target.value})} data-testid="pest-corrective" />
          <Button onClick={handleSubmit} className="bg-[#7B2D3F] hover:bg-[#7B2D3F]/90" data-testid="pest-submit">Registrar incidència</Button>
        </Card>
      )}

      {/* Incident List */}
      <div className="space-y-2">
        {incidents.length === 0 ? (
          <Card className="p-8 border-0 shadow-sm text-center">
            <Bug className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">Cap incidència de plagues registrada.</p>
          </Card>
        ) : incidents.map((inc, i) => {
          const sev = SEVERITY_OPTS.find(s => s.value === inc.severity) || SEVERITY_OPTS[1];
          const pt = PEST_TYPES.find(t => t.value === inc.pest_type)?.label || inc.pest_type;
          const isClosed = inc.status === "closed" || inc.status === "resolved";
          return (
            <Card key={inc.id || inc.pest_incident_id || i} className={`p-4 border-0 shadow-sm ${isClosed ? "opacity-60" : ""}`} data-testid={`pest-incident-${i}`}>
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${isClosed ? "bg-emerald-100" : "bg-red-50"}`}>
                  {isClosed ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <Bug className="w-5 h-5 text-red-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-gray-900">{pt}</p>
                    <Badge className={`${sev.color} border-0 text-[10px]`}>{sev.label}</Badge>
                    <Badge className="bg-gray-100 text-gray-600 border-0 text-[10px]">{inc.location}</Badge>
                    <Badge className={`border-0 text-[10px] ${isClosed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {isClosed ? "Tancada" : "Oberta"}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{inc.description}</p>
                  {inc.corrective_action && <p className="text-xs text-blue-600 mt-1">Correctiva: {inc.corrective_action}</p>}
                  <div className="flex items-center gap-3 mt-2 text-[10px] text-gray-400">
                    <span><Calendar className="w-3 h-3 inline mr-0.5" />{inc.date}</span>
                    {inc.verified && <span><ClipboardCheck className="w-3 h-3 inline mr-0.5 text-emerald-500" />Verificada</span>}
                  </div>
                </div>
                {!isClosed && (
                  <div className="flex gap-1 flex-shrink-0">
                    {!inc.verified && (
                      <button onClick={() => handleVerify(inc)} className="text-xs text-blue-600 hover:bg-blue-50 px-2 py-1 rounded-lg" data-testid={`verify-pest-${i}`}>Verificar</button>
                    )}
                    <button onClick={() => handleClose(inc)} className="text-xs text-emerald-600 hover:bg-emerald-50 px-2 py-1 rounded-lg" data-testid={`close-pest-${i}`}>Tancar</button>
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
